import { now } from './auctionClock';

/**
 * Date helpers for auction lots (spec 006, research.md R5).
 *
 * This module used to carry four parsers, because apiauctions.io encoded dates
 * four different ways — three of them for the same concept, inside a single lot
 * (epoch ms as a string, epoch seconds as a number, bare "YYYYMMDD", and a
 * non-ISO "YYYY-MM-DD HH:MM:SS"). Reading seconds as milliseconds lands in 1970;
 * the reverse lands ~55,000 years out, so each shape had its own parser behind a
 * plausibility window that returned null rather than render a wrong date
 * (FR-012, SC-008).
 *
 * apibara has one encoding — ISO strings in `auction_at` and `updated_at` — so
 * `normalizeApibaraLot` parses them itself and all four parsers went unused when
 * spec 008 deleted the other provider. **Their plausibility window went with
 * them: nothing currently range-checks a parsed date.** If a provider ever
 * returns a date outside a sane window again, the guard belongs in the
 * normaliser, not here.
 *
 * What is left is display and comparison, which no provider owns.
 */

/**
 * Every date is DISPLAYED in Polish time, whatever machine renders it.
 *
 * `auction_at` is a true instant (the provider's own `diff_minutes` agrees with
 * reading it as UTC — verified on lot 49052296, spec 013), so the countdown is
 * timezone-proof by construction. The calendar date is not: a Honolulu sale at
 * 12:00 local is 22:00 UTC and 00:00 the NEXT day in Poland, and `Intl` with
 * no `timeZone` answers in whatever zone the server or browser happens to be
 * in. Pinning the zone means the server, the browser and two visitors in
 * different countries all name the same day — the day the visitor, who is in
 * Poland, would have to be awake on.
 */
export const AUCTION_DISPLAY_TIME_ZONE = 'Europe/Warsaw';

/** Polish calendar date for display (FR-012, FR-013). */
export const formatAuctionDate = (date: Date | null): string | null =>
  date
    ? new Intl.DateTimeFormat('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: AUCTION_DISPLAY_TIME_ZONE,
      }).format(date)
    : null;

/** "5 września 2026, 00:00" — the date with the clock time, in Polish time. */
export const formatAuctionDateTime = (date: Date | null): string | null =>
  date
    ? `${formatAuctionDate(date)}, ${new Intl.DateTimeFormat('pl-PL', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: AUCTION_DISPLAY_TIME_ZONE,
      }).format(date)}`
    : null;

/** The instant the sale's calendar day ends — in Polish time, not the server's. */
const endOfDisplayDay = (date: Date): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: AUCTION_DISPLAY_TIME_ZONE,
  }).formatToParts(date);
  const read = (type: string) => Number(parts.find(part => part.type === type)?.value ?? 0);
  const elapsedMs =
    (read('hour') * 3600 + read('minute') * 60 + read('second')) * 1000 + date.getMilliseconds();

  return date.getTime() + (86_400_000 - 1) - elapsedMs;
};

/**
 * Has the auction date actually elapsed?
 *
 * Compares against the END of the sale's calendar day, not the instant. The
 * provider's sale_date lands at the start of the auction day, so a plain
 * `date < now` marked every auction happening TODAY as already missed — for the
 * whole day, on lots that were still perfectly biddable.
 */
export const isPast = (date: Date | null): boolean => {
  if (!date) return false;

  return endOfDisplayDay(date) < now();
};
